import { useEffect, useRef } from "react";
import { Eye, EyeOff, LogOut } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { quickExit, useVisitor } from "@/lib/visitor";

/**
 * Discreet browsing controls (a LUX differentiator):
 *  - Quick exit: leaves LUX for a neutral site instantly (button, or press Escape twice).
 *  - Blur photos: every photo on the site is blurred until you hover / reveal it.
 */
export default function DiscreetBar() {
  const { blur, setBlur } = useVisitor();
  const lastEsc = useRef(0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const t = Date.now();
      if (t - lastEsc.current < 900) quickExit();
      lastEsc.current = t;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <TooltipProvider delayDuration={200}>
      <div className="fixed bottom-4 left-4 z-30 flex items-center gap-1 rounded-full border border-lux-border bg-[#0B0B0C]/85 p-1 shadow-lux backdrop-blur supports-[backdrop-filter]:bg-[#0B0B0C]/70" data-testid="discreet-bar" role="toolbar" aria-label="Discreet browsing">
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={quickExit} className="inline-flex h-10 items-center gap-2 rounded-full bg-lux-silver px-3.5 text-[13px] font-medium text-black hover:bg-lux-silverBright" data-testid="quick-exit-button" aria-label="Quick exit: leave LUX immediately">
              <LogOut className="h-4 w-4" aria-hidden="true" /> <span>Quick exit</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="border-lux-border bg-lux-surface text-lux-text">Leaves LUX for a neutral site. Or press Escape twice.</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => setBlur(!blur)} aria-pressed={blur} className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${blur ? "bg-lux-surface2 text-lux-silverBright" : "text-lux-silver hover:bg-white/5 hover:text-lux-silverBright"}`} data-testid="discreet-blur-toggle" aria-label={blur ? "Photos are blurred. Show photos" : "Blur photos while browsing"}>
              {blur ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="border-lux-border bg-lux-surface text-lux-text">{blur ? "Photos are blurred (hover or tap to reveal)" : "Blur photos while you browse"}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
